import {FunctionComponent, HTMLAttributes, useCallback, useState} from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import HelpIcon from '@/assets/images/icon-help.svg?react';
import Button from '@/components/button';
import AssistiveText from '@/components/form/assistive-text';
import Label from '@/components/form/label';
import {BUTTON_VARIANT} from '@/constants/button';
import {firstNotBlankOrDefault} from '@/engine/utils/string';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {useFormControlContext} from '../form-control/form-control';
import HelperDialog from './helper-dialog';

type ElementProps = HTMLAttributes<HTMLDivElement> & {
  assistiveText?: string;
  assistiveTextClassName?: string;
  showAssistiveText?: boolean;
  useFieldset?: boolean;
  helpDialogTitle?: string;
  helpDialogToggleAccessibilityLabel?: string;
  helpDialogContent?: string;
  identifier: string;
  label?: string;
  labelMultiline?: boolean;
};

type WrapperProps =
  | ({useFieldset: false} & HTMLAttributes<HTMLDivElement>)
  | ({useFieldset: true} & HTMLAttributes<HTMLFieldSetElement>);

const WrapperComponent = (props: WrapperProps) => {
  if (props.useFieldset) {
    const {useFieldset, ...rest} = props;
    return <fieldset {...rest} />;
  } else {
    const {useFieldset, ...rest} = props;
    return <div {...rest} />;
  }
};

const FieldWrapper: FunctionComponent<ElementProps> = ({
  assistiveText,
  assistiveTextClassName,
  showAssistiveText = true,
  useFieldset,
  helpDialogTitle,
  helpDialogToggleAccessibilityLabel,
  helpDialogContent,
  hidden,
  identifier,
  label,
  labelMultiline = true,
  children,
}) => {
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const {errors, warningMessage} = useFormControlContext();

  const onShowDialog = useCallback(() => {
    setIsDialogVisible(true);
  }, []);

  const onCloseDialog = useCallback(() => {
    setIsDialogVisible(false);
  }, []);

  const subText = firstNotBlankOrDefault([_.head(errors), assistiveText, warningMessage], '');

  return (
    <WrapperComponent
      data-testid={identifier}
      useFieldset={!!useFieldset}
      className={clsx('flex grow flex-col', hidden && 'hidden')}
    >
      {label && (
        <Label
          multiline={labelMultiline}
          inFieldset={!!useFieldset}
          htmlFor={identifier}
          data-testid={`${identifier}-label`}
        >
          {label}
        </Label>
      )}

      <div className="flex w-full grow items-center gap-4">
        {children}

        {helpDialogContent && (
          <Button
            data-testid={`${identifier}-helper`}
            aria-label={helpDialogToggleAccessibilityLabel}
            onClick={onShowDialog}
            variant={BUTTON_VARIANT.SMALL}
            icon={<HelpIcon className="size-5 shrink-0 fill-violet" />}
            trackingKey={WEB_CLICK_TRACKING_KEY.OPEN_FIELD_HELP}
          />
        )}
      </div>

      {showAssistiveText && (
        <AssistiveText data-testid={`${identifier}_assistive-text`} className={clsx('mt-1', assistiveTextClassName)}>
          {subText}
        </AssistiveText>
      )}

      <HelperDialog
        isVisible={isDialogVisible}
        onClose={onCloseDialog}
        title={helpDialogTitle}
        content={helpDialogContent}
        identifier={identifier}
      />
    </WrapperComponent>
  );
};

export default FieldWrapper;
